import dayjs from 'dayjs'

/**
 * 補齊缺失的日期
 *
 * @param data Server 回傳的資料
 * @param startDate 起始日期
 * @param endDate 結束日期
 * @param defaultValues 預設值
 * @param includeTimeRange 補上的預設值是否要一併帶 startTime/endTime（新系統 v2 資料才有這兩個
 *   欄位；舊系統 v1 只有 date，補的空值列也應該只有 date，避免下游誤判成 v2 資料）
 * @returns 補齊後的資料
 */
export function fillMissingDates<T extends Record<string, any>>(
  data: T[],
  startDate: string,
  endDate: string,
  defaultValues: Omit<T, 'date'>,
  includeTimeRange = false
): T[] {
  if (!data) {
    return []
  }

  // 補齊的單位是「日曆天」，跟 startDate/endDate 本身帶的時分秒無關（例如帳務查詢頁併入
  // 小時篩選後，startDate/endDate 會是 datetime；每天的實際資料時分秒也可能跟請求時間不同），
  // 一律以天為粒度比對，避免因時分秒不一致誤判成整段都缺資料
  const start = dayjs(startDate).startOf('day')
  const end = dayjs(endDate).startOf('day')

  // 新系統一筆資料 = 一個結算日（當天結帳小時 ～ 隔天同一個小時），補值要跟著同一個小時，
  // 否則補出來的那筆會變成另一種一天；startDate 只有日期（v1、或結帳時間就是 00:00）時
  // 本來就是 00:00:00
  const settlementTime = dayjs(startDate).format('HH:mm:ss')

  // 計算請求的日期天數（含起訖）
  const totalDays = end.diff(start, 'day') + 1

  // 若 Server 資料已經完整，不補齊
  if (data.length >= totalDays) {
    return data
  }

  // 排序
  data.sort((a, b) => a.date.localeCompare(b.date))

  // 把資料轉成 Map，key 只取日期部分
  const map = new Map<string, T>(data.map((item) => [dayjs(item.date).format('YYYY-MM-DD'), item]))

  // 逐天數補齊
  const result: T[] = []
  let current = start

  while (current.isBefore(end) || current.isSame(end)) {
    const dayKey = current.format('YYYY-MM-DD')
    const existing = map.get(dayKey)

    // 若無資料，補上預設值；includeTimeRange 時比照真實 v2 資料代表一個完整結算日
    // （當天結帳小時 ～ 隔天同一個小時）一併補上 startTime/endTime，v1 只有 date，
    // 維持原樣不補這兩個欄位
    if (!existing) {
      const dateStr = `${current.format('YYYY-MM-DD')}T${settlementTime}`
      result.push({
        date: dateStr,
        ...(includeTimeRange
          ? {
              startTime: dateStr,
              endTime: `${current.add(1, 'day').format('YYYY-MM-DD')}T${settlementTime}`
            }
          : {}),
        ...defaultValues
      } as unknown as T)
    } else {
      result.push(existing)
    }

    current = current.add(1, 'day')
  }

  return result
}

/**
 * fillMissingDates 補值用的真實日曆天終點
 *
 * endDate 可能是排他性查詢邊界（隔天 00:00，涵蓋今天整天用），但真實資料永遠不可能存在於未來，
 * 所以只要 endDate 等於「明天」，不論是快捷篩選位移、resetFilter 位移、還是使用者手動選到明天，
 * 實際涵蓋到的日曆天終點必然是今天。這個值不能直接拿去當 fillMissingDates 補值的日曆天終點，
 * 否則會多補出不存在的隔天資料。
 *
 * @param endDate 查詢用的結束日期，可能是排他性邊界
 * @returns 真實日曆天終點
 */
export function getFillEndDate(endDate: string | null): string | null {
  const tomorrow = dayjs().add(1, 'day').format('YYYY-MM-DD')
  return endDate === tomorrow ? dayjs().format('YYYY-MM-DD') : endDate
}
